// src/features/beitragsrechnung/components/BeitragsrechnungPage.tsx
//
// Beitragsrechnung (30.10.2026): Stufenrechnung bis zum Überschuss, Überleitung
// zur BWA, Gewinnverwendung und Tilgungen des Darlehens der Dr. Priel AG.
// In der Gewinnverwendung erhält Tim Wolter Fixvergütung und Dienstwagenbudget
// vorab als feste Beträge; der Rest wird geteilt. Getrennt davon zeigt die Seite,
// was zugewiesen, bereits erhalten und noch offen ist (Vorgabe Tim, 30.10.2026),
// sowie eine Prognose der Einkommensteuer beider Gesellschafter (03.11.2026).
// Sichtbar nur für Mitarbeiter mit dem Merkmal darf_beitragsrechnung_sehen; die
// Berechtigung prüft die Netlify-Funktion beitragsrechnung anhand der
// persönlichen Anmeldung. Alle Werte berechnet die Funktion; die Seite summiert
// nur über den gewählten Zeitraum.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Loader, Trash2, X } from 'lucide-react';
import { beitragsrechnungService } from '../services/beitragsrechnungService';
import {
  BeitragsrechnungBuchung,
  BeitragsrechnungDaten,
  BeitragsrechnungMonat,
  BuchungsRegel,
  DetailKategorie,
  GemeinkostenKategorie
} from '../types';

const MONATE: string[] = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONATE_KURZ: string[] = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const GK_REIHENFOLGE: GemeinkostenKategorie[] = [
  'gk_software',
  'gk_raum',
  'gk_edv_dienstleister',
  'gk_buero_sonstiges',
  'gk_versicherungen_beitraege',
  'gk_werbung_reise',
  'gk_personalnebenkosten',
  'gk_erstattungen_kk'
];

const KATEGORIE_LABEL: Record<DetailKategorie, string> = {
  gk_software: 'Software ohne DATEV',
  gk_raum: 'Raum',
  gk_edv_dienstleister: 'EDV-Dienstleister',
  gk_buero_sonstiges: 'Büro, Kommunikation, Sonstiges',
  gk_versicherungen_beitraege: 'Versicherungen und Beiträge',
  gk_werbung_reise: 'Werbung und Reisen',
  gk_personalnebenkosten: 'Personalnebenkosten',
  gk_erstattungen_kk: 'Erstattungen der Krankenkassen',
  zinsen: 'Zinsen Darlehen Dr. Priel AG',
  gf_firmenwagen: 'Firmenwagen',
  gf_sonstiges: 'Weitere Zuwendungen Tim Wolter (Visiolife, Mobilfunk privat)',
  datev_rueckverguetung: 'DATEV-Rückvergütung'
};

const REGEL_LABEL: Record<BuchungsRegel, string> = {
  belegdatum: 'Monat des Belegs',
  buchungstext: 'Monat laut Buchungstext',
  regus_folgemonat: 'Regus: Folgemonat',
  regus_vormonat: 'Regus: Vormonat',
  telekom_vormonat: 'Telekom: Vormonat',
  verteilung: 'verteilt',
  storno_originalmonat: 'Storno im Originalmonat',
  manuell: 'Zuordnung auf Entscheidung'
};

const euroFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const zahlFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });
const prozentFormat = new Intl.NumberFormat('de-DE', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });

// Beträge unter einem halben Cent als 0 anzeigen, ohne Minuszeichen
const euro = (n: number): string => euroFormat.format(Math.abs(n) < 0.005 ? 0 : n);
const zahl = (n: number): string => zahlFormat.format(n);
const quote = (teil: number, ganzes: number): string => (ganzes !== 0 ? prozentFormat.format(teil / ganzes) : '');
const r2 = (n: number): number => Math.round(n * 100) / 100;

const formatDatum = (iso: string | null): string => {
  if (!iso) return '';
  const [jahr, monat, tag] = iso.slice(0, 10).split('-');
  return `${tag}.${monat}.${jahr}`;
};

/** Liest einen Betrag in deutscher Schreibweise ("1.234,56") */
const leseBetrag = (eingabe: string): number => {
  const bereinigt = eingabe.trim().replace(/\./g, '').replace(',', '.');
  return bereinigt === '' ? NaN : Number(bereinigt);
};

// ============================================
// Stile
// ============================================

const karte: React.CSSProperties = { backgroundColor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: '20px' };
const kartenKopf: React.CSSProperties = { padding: '14px 20px', borderBottom: '1px solid #E5E7EB', fontSize: '15px', fontWeight: 600, color: '#111827' };
const th: React.CSSProperties = { padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap', backgroundColor: '#F9FAFB' };
const thLinks: React.CSSProperties = { ...th, textAlign: 'left' };
const td: React.CSSProperties = { padding: '9px 14px', textAlign: 'right', color: '#374151', whiteSpace: 'nowrap', borderBottom: '1px solid #F3F4F6' };
const tdLinks: React.CSSProperties = { ...td, textAlign: 'left', color: '#111827' };
const zeileStufe: React.CSSProperties = { backgroundColor: '#F9FAFB', fontWeight: 600 };
const zeileErgebnis: React.CSSProperties = { backgroundColor: '#ECFDF5', fontWeight: 700 };
const knopf: React.CSSProperties = { padding: '6px 10px', backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' };
const auswahlfeld: React.CSSProperties = { padding: '6px 10px', fontSize: '14px', backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: '6px', color: '#111827' };

const farbeBetrag = (n: number): string => (n <= -0.005 ? '#DC2626' : '#111827');

// ============================================
// Hinweiszeile
// ============================================

const HINWEIS_FARBEN: Record<'rot' | 'gelb' | 'grau', { bg: string; rand: string; text: string }> = {
  rot: { bg: '#FEF2F2', rand: '#FECACA', text: '#991B1B' },
  gelb: { bg: '#FFFBEB', rand: '#FDE68A', text: '#92400E' },
  grau: { bg: '#F9FAFB', rand: '#E5E7EB', text: '#6B7280' }
};

const Hinweis: React.FC<{ farbe: 'rot' | 'gelb' | 'grau'; children: React.ReactNode }> = ({ farbe, children }) => {
  const f = HINWEIS_FARBEN[farbe];
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '10px 14px', backgroundColor: f.bg, border: `1px solid ${f.rand}`, borderRadius: '10px', color: f.text, fontSize: '13px', lineHeight: '1.5' }}>
      <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '2px' }} />
      <span>{children}</span>
    </div>
  );
};

export const BeitragsrechnungPage: React.FC = () => {
  const aktuellesJahr = new Date().getFullYear();
  const [jahr, setJahr] = useState<number>(aktuellesJahr);
  const [von, setVon] = useState<number>(1);
  const [bis, setBis] = useState<number>(12);
  const [daten, setDaten] = useState<BeitragsrechnungDaten | null>(null);
  const [laedt, setLaedt] = useState<boolean>(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gkOffen, setGkOffen] = useState<boolean>(false);
  const [steuerHerleitungOffen, setSteuerHerleitungOffen] = useState<boolean>(false);

  const [detail, setDetail] = useState<DetailKategorie | null>(null);
  const [detailBuchungen, setDetailBuchungen] = useState<BeitragsrechnungBuchung[]>([]);
  const [detailLaedt, setDetailLaedt] = useState<boolean>(false);
  const [detailFehler, setDetailFehler] = useState<string | null>(null);

  const [tilgungMonat, setTilgungMonat] = useState<number>(new Date().getMonth() + 1);
  const [tilgungBetrag, setTilgungBetrag] = useState<string>('');
  const [tilgungNotiz, setTilgungNotiz] = useState<string>('');
  const [tilgungArbeitet, setTilgungArbeitet] = useState<boolean>(false);
  const [tilgungFehler, setTilgungFehler] = useState<string | null>(null);

  const ladeDaten = useCallback(async (zieljahr: number, zeitraumSetzen: boolean) => {
    setLaedt(true);
    setFehler(null);
    try {
      const ergebnis = await beitragsrechnungService.ladeJahr(zieljahr);
      setDaten(ergebnis);
      if (zeitraumSetzen) {
        const geladen = ergebnis.monate.filter(m => m.geladen).map(m => m.monat);
        setVon(1);
        setBis(geladen.length > 0 ? Math.max(...geladen) : 12);
      }
    } catch (error) {
      setDaten(null);
      setFehler(error instanceof Error ? error.message : String(error));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    ladeDaten(jahr, true);
  }, [jahr, ladeDaten]);

  const handleVon = (wert: number) => {
    setVon(wert);
    if (wert > bis) setBis(wert);
  };

  const handleBis = (wert: number) => {
    setBis(wert);
    if (wert < von) setVon(wert);
  };

  // Monate des Zeitraums, für die der Buchungsstapel geladen ist
  const auswahl = useMemo<BeitragsrechnungMonat[]>(() => {
    if (!daten) return [];
    return daten.monate.filter(m => m.geladen && m.monat >= von && m.monat <= bis);
  }, [daten, von, bis]);

  const nichtGeladen = useMemo<number[]>(() => {
    if (!daten) return [];
    return daten.monate.filter(m => !m.geladen && m.monat >= von && m.monat <= bis).map(m => m.monat);
  }, [daten, von, bis]);

  const summe = useCallback((wert: (m: BeitragsrechnungMonat) => number): number => (
    r2(auswahl.reduce((s, m) => s + wert(m), 0))
  ), [auswahl]);

  const s = useMemo(() => {
    const umsatz = summe(m => m.umsatz);
    const datevDirekt = summe(m => m.datev_direkt);
    const personalAbrechnend = summe(m => m.personal_abrechnend);
    const support = summe(m => m.support);
    const datevAllgemein = summe(m => m.datev_allgemein);
    const gemeinkosten = summe(m => m.gemeinkosten);
    const zinsen = summe(m => m.zinsen);
    const dbI = r2(umsatz - datevDirekt);
    const dbII = r2(dbI - personalAbrechnend);
    const dbIII = r2(dbII - support - datevAllgemein);
    const dbIV = r2(dbIII - gemeinkosten);
    const gk: Record<GemeinkostenKategorie, number> = {} as Record<GemeinkostenKategorie, number>;
    for (const k of GK_REIHENFOLGE) gk[k] = summe(m => m.gemeinkosten_je_kategorie[k] || 0);
    return {
      umsatz,
      umsatzGf: summe(m => m.umsatz_gf),
      umsatzOhneZuordnung: summe(m => m.umsatz_ohne_zuordnung),
      abrechnungen: summe(m => m.abrechnungen),
      datevDirekt,
      personalAbrechnend,
      support,
      datevAllgemein,
      gemeinkosten,
      gk,
      zinsen,
      dbI,
      dbII,
      dbIII,
      dbIV,
      ueberschuss: summe(m => m.ueberschuss)
    };
  }, [summe]);

  const oeffneDetail = async (kategorie: DetailKategorie) => {
    setDetail(kategorie);
    setDetailBuchungen([]);
    setDetailFehler(null);
    setDetailLaedt(true);
    try {
      setDetailBuchungen(await beitragsrechnungService.ladeBuchungen(jahr, von, bis, kategorie));
    } catch (error) {
      setDetailFehler(error instanceof Error ? error.message : String(error));
    } finally {
      setDetailLaedt(false);
    }
  };

  const speichereTilgung = async () => {
    const betrag = leseBetrag(tilgungBetrag);
    if (!Number.isFinite(betrag) || betrag <= 0) {
      setTilgungFehler('Bitte einen Betrag größer als 0 eingeben.');
      return;
    }
    setTilgungArbeitet(true);
    setTilgungFehler(null);
    try {
      await beitragsrechnungService.speichereTilgung(jahr, tilgungMonat, r2(betrag), tilgungNotiz);
      setTilgungBetrag('');
      setTilgungNotiz('');
      await ladeDaten(jahr, false);
    } catch (error) {
      setTilgungFehler(error instanceof Error ? error.message : String(error));
    } finally {
      setTilgungArbeitet(false);
    }
  };

  const loescheTilgung = async (id: string) => {
    if (!window.confirm('Diese Tilgung löschen?')) return;
    setTilgungArbeitet(true);
    setTilgungFehler(null);
    try {
      await beitragsrechnungService.loescheTilgung(id);
      await ladeDaten(jahr, false);
    } catch (error) {
      setTilgungFehler(error instanceof Error ? error.message : String(error));
    } finally {
      setTilgungArbeitet(false);
    }
  };

  // ============================================
  // Hinweise zu Datenstand und Kontrolle
  // ============================================

  const vorlaeufig = auswahl.filter(m => m.stand === 'vorlaeufig').map(m => m.monat);
  const ohneDatev = auswahl.filter(m => !m.quellen.datev_rechnung).map(m => m.monat);
  const ohnePersonal = auswahl.filter(m => !m.quellen.personal).map(m => m.monat);
  const mitAbweichung = auswahl.filter(m => m.ueberleitung.abweichung_ueberleitung !== 0 || (m.ueberleitung.abweichung_kontrollwert !== null && m.ueberleitung.abweichung_kontrollwert !== 0)).map(m => m.monat);
  const monatsliste = (liste: number[]) => liste.map(m => MONATE[m]).join(', ');

  // ============================================
  // Bausteine
  // ============================================

  const StufenZeile: React.FC<{ label: React.ReactNode; betrag: number; stil?: React.CSSProperties; kosten?: boolean; onClick?: () => void }> = ({ label, betrag, stil, kosten, onClick }) => (
    <tr style={{ ...stil, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <td style={tdLinks}>{label}</td>
      <td style={{ ...td, color: kosten ? '#DC2626' : farbeBetrag(betrag), fontWeight: stil ? 'inherit' : 500 }}>{euro(betrag)}</td>
      <td style={td}>{s.abrechnungen > 0 ? euro(betrag / s.abrechnungen) : ''}</td>
      <td style={td}>{quote(betrag, s.umsatz)}</td>
    </tr>
  );

  const MonatsZeile: React.FC<{ label: string; wert: (m: BeitragsrechnungMonat) => number; stil?: React.CSSProperties }> = ({ label, wert, stil }) => {
    const gesamt = summe(wert);
    return (
      <tr style={stil}>
        <td style={tdLinks}>{label}</td>
        {auswahl.map(m => (
          <td key={m.monat} style={{ ...td, color: farbeBetrag(wert(m)) }}>{euro(wert(m))}</td>
        ))}
        <td style={{ ...td, fontWeight: 600, color: farbeBetrag(gesamt) }}>{euro(gesamt)}</td>
      </tr>
    );
  };

  const GruppenZeile: React.FC<{ label: string }> = ({ label }) => (
    <tr>
      <td style={{ ...tdLinks, fontWeight: 700, paddingTop: '14px', borderBottom: '1px solid #E5E7EB' }} colSpan={auswahl.length + 2}>{label}</td>
    </tr>
  );

  const MonatsKopf: React.FC = () => (
    <thead>
      <tr>
        <th style={thLinks}></th>
        {auswahl.map(m => (
          <th key={m.monat} style={th}>
            {MONATE_KURZ[m.monat]}
            {m.stand === 'vorlaeufig' && <span style={{ color: '#B45309' }}> *</span>}
          </th>
        ))}
        <th style={th}>Summe</th>
      </tr>
    </thead>
  );

  const zugewiesenWolter = summe(m => m.zugewiesen_wolter || 0);
  const erhaltenWolter = summe(m => m.gf.gesamt);
  const offenWolter = summe(m => m.offen_wolter || 0);
  const anteilPriel = summe(m => m.anteil_priel || 0);

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', padding: '20px 0' }}>
      {/* Kopf */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', margin: 0 }}>Beitragsrechnung</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button style={knopf} onClick={() => setJahr(j => j - 1)} disabled={laedt}><ArrowLeft size={16} /></button>
            <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827', minWidth: '50px', textAlign: 'center' }}>{jahr}</span>
            <button style={knopf} onClick={() => setJahr(j => j + 1)} disabled={laedt || jahr >= aktuellesJahr}><ArrowRight size={16} /></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>von</span>
            <select value={von} onChange={e => handleVon(Number(e.target.value))} style={auswahlfeld}>
              {MONATE.slice(1).map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
            </select>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>bis</span>
            <select value={bis} onChange={e => handleBis(Number(e.target.value))} style={auswahlfeld}>
              {MONATE.slice(1).map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {laedt && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', color: '#6B7280' }}>
          <Loader size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
          Wird berechnet...
        </div>
      )}

      {!laedt && fehler && (
        <div style={{ padding: '16px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', color: '#991B1B', fontSize: '14px' }}>
          {fehler}
        </div>
      )}

      {!laedt && !fehler && daten && auswahl.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF', fontSize: '14px' }}>
          Für den gewählten Zeitraum sind keine Buchungen geladen.
        </div>
      )}

      {!laedt && !fehler && daten && auswahl.length > 0 && (
        <>
          {/* Hinweise */}
          {(nichtGeladen.length > 0 || vorlaeufig.length > 0 || ohneDatev.length > 0 || ohnePersonal.length > 0 || mitAbweichung.length > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {mitAbweichung.length > 0 && (
                <Hinweis farbe="rot">
                  Die Überleitung zur BWA geht für {monatsliste(mitAbweichung)} nicht auf. Bitte die Daten dieser Monate prüfen.
                </Hinweis>
              )}
              {ohneDatev.length > 0 && (
                <Hinweis farbe="gelb">Für {monatsliste(ohneDatev)} ist keine DATEV-Rechnung erfasst. Die DATEV-Kosten fehlen in diesen Monaten.</Hinweis>
              )}
              {ohnePersonal.length > 0 && (
                <Hinweis farbe="gelb">Für {monatsliste(ohnePersonal)} sind keine Personalkosten aus dem Lohnjournal erfasst.</Hinweis>
              )}
              {vorlaeufig.length > 0 && (
                <Hinweis farbe="gelb">{monatsliste(vorlaeufig)} ist vorläufig (*), bis der Buchungsstapel des Folgemonats eingespielt ist.</Hinweis>
              )}
              {nichtGeladen.length > 0 && (
                <Hinweis farbe="grau">Für {monatsliste(nichtGeladen)} liegen noch keine Buchungen vor; diese Monate sind nicht enthalten.</Hinweis>
              )}
            </div>
          )}

          {/* Stufenrechnung */}
          <div style={karte}>
            <div style={kartenKopf}>
              Beitragsrechnung {MONATE[auswahl[0].monat]} bis {MONATE[auswahl[auswahl.length - 1].monat]} {jahr}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th style={thLinks}>Stufe</th>
                    <th style={th}>Betrag</th>
                    <th style={th}>je Abrechnung</th>
                    <th style={th}>Anteil Umsatz</th>
                  </tr>
                </thead>
                <tbody>
                  <StufenZeile
                    label={<>Umsatz <span style={{ color: '#9CA3AF' }}>({zahl(s.abrechnungen)} Abrechnungen)</span></>}
                    betrag={s.umsatz}
                  />
                  {s.umsatzGf !== 0 && (
                    <StufenZeile label={<span style={{ paddingLeft: '16px', color: '#6B7280' }}>davon Umsatz Geschäftsführung</span>} betrag={s.umsatzGf} />
                  )}
                  {s.umsatzOhneZuordnung !== 0 && (
                    <StufenZeile label={<span style={{ paddingLeft: '16px', color: '#6B7280' }}>davon ohne Mitarbeiterzuordnung</span>} betrag={s.umsatzOhneZuordnung} />
                  )}
                  <StufenZeile label="DATEV mandantenbezogen" betrag={s.datevDirekt} kosten />
                  <StufenZeile label="DB I" betrag={s.dbI} stil={zeileStufe} />
                  <StufenZeile label="Personal abrechnend" betrag={s.personalAbrechnend} kosten />
                  <StufenZeile label="DB II" betrag={s.dbII} stil={zeileStufe} />
                  <StufenZeile label="Support Staff" betrag={s.support} kosten />
                  <StufenZeile label="DATEV allgemein" betrag={s.datevAllgemein} kosten />
                  <StufenZeile label="DB III" betrag={s.dbIII} stil={zeileStufe} />
                  <StufenZeile
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {gkOffen ? <ChevronDown size={14} color="#6B7280" /> : <ChevronRight size={14} color="#6B7280" />}
                        Gemeinkosten
                      </span>
                    }
                    betrag={s.gemeinkosten}
                    kosten
                    onClick={() => setGkOffen(o => !o)}
                  />
                  {gkOffen && GK_REIHENFOLGE.map(k => (
                    <StufenZeile
                      key={k}
                      label={<span style={{ paddingLeft: '22px', color: '#2563EB' }}>{KATEGORIE_LABEL[k]}</span>}
                      betrag={s.gk[k]}
                      onClick={() => oeffneDetail(k)}
                    />
                  ))}
                  <StufenZeile label="DB IV" betrag={s.dbIV} stil={zeileStufe} />
                  <StufenZeile
                    label={<span style={{ color: '#2563EB' }}>Zinsen Darlehen Dr. Priel AG</span>}
                    betrag={s.zinsen}
                    kosten
                    onClick={() => oeffneDetail('zinsen')}
                  />
                  <StufenZeile label="Überschuss" betrag={s.ueberschuss} stil={zeileErgebnis} />
                </tbody>
              </table>
            </div>
            <div style={{ padding: '10px 16px', fontSize: '12px', color: '#9CA3AF', borderTop: '1px solid #E5E7EB' }}>
              Blau markierte Zeilen zeigen beim Anklicken die einzelnen Buchungen. Jede Buchung steht in dem Monat, den sie betrifft.
            </div>
          </div>

          {/* Überleitung zur BWA */}
          <div style={karte}>
            <div style={kartenKopf}>Überleitung zur BWA</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <MonatsKopf />
                <tbody>
                  <MonatsZeile label="Überschuss" wert={m => m.ueberschuss} stil={zeileStufe} />
                  <MonatsZeile label="Geschäftsführerkosten" wert={m => -m.gf.gesamt} />
                  <MonatsZeile label="Personal nicht im Lohnjournal" wert={m => -m.ueberleitung.personal_ohne_journal} />
                  <MonatsZeile label="Umsatz in anderem Monat gebucht" wert={m => m.ueberleitung.umsatz} />
                  <MonatsZeile label="DATEV-Buchung abweichend von der Rechnung" wert={m => -m.ueberleitung.datev} />
                  <MonatsZeile label="Kosten in anderem Monat gebucht" wert={m => m.ueberleitung.kosten_zeitlich} />
                  <MonatsZeile label="Ergebnis laut BWA" wert={m => m.ueberleitung.bwa_berechnet} stil={zeileErgebnis} />
                  <tr>
                    <td style={{ ...tdLinks, color: '#6B7280' }}>Kontrolle gegen BWA</td>
                    {auswahl.map(m => {
                      const ok = m.ueberleitung.abweichung_ueberleitung === 0 && m.ueberleitung.abweichung_kontrollwert === 0;
                      return (
                        <td key={m.monat} style={{ ...td, color: ok ? '#059669' : '#DC2626' }}>
                          {ok ? 'stimmt' : euro((m.ueberleitung.abweichung_kontrollwert || 0) + m.ueberleitung.abweichung_ueberleitung)}
                        </td>
                      );
                    })}
                    <td style={td}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Gewinnverwendung */}
          <div style={karte}>
            <div style={kartenKopf}>Gewinnverwendung</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <MonatsKopf />
                <tbody>
                  <MonatsZeile label="Überschuss" wert={m => m.ueberschuss} stil={zeileStufe} />
                  <MonatsZeile label={`Rückstellung Gewerbesteuer (${zahl(daten.einstellungen.gewst_hebesatz)} %)`} wert={m => -(m.gewerbesteuer || 0)} />
                  <MonatsZeile label="Tilgung Darlehen Dr. Priel AG" wert={m => -m.tilgung} />
                  <MonatsZeile label="Zur Verteilung" wert={m => m.zur_verteilung || 0} stil={zeileStufe} />
                  <MonatsZeile label="Vorab Tim Wolter: Fixvergütung" wert={() => -daten.einstellungen.gf_fixverguetung} />
                  <MonatsZeile label="Vorab Tim Wolter: Dienstwagenbudget" wert={() => -daten.einstellungen.gf_dienstwagenbudget} />
                  <MonatsZeile label="Rest" wert={m => m.rest || 0} stil={zeileErgebnis} />
                  <MonatsZeile label={`Anteil Eike Priel (${zahl(daten.einstellungen.verteilung_priel)} %)`} wert={m => m.anteil_priel || 0} />
                  <MonatsZeile label={`Anteil Tim Wolter (${zahl(daten.einstellungen.verteilung_wolter)} %)`} wert={m => m.anteil_wolter || 0} />
                </tbody>
              </table>
            </div>
          </div>

          {/* Zuweisung und Auszahlung */}
          <div style={karte}>
            <div style={kartenKopf}>Zuweisung und Auszahlung</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <MonatsKopf />
                <tbody>
                  <GruppenZeile label="Tim Wolter" />
                  <MonatsZeile label="Vorab Fixvergütung und Dienstwagenbudget" wert={m => m.gf_fest || 0} />
                  <MonatsZeile label="Anteil am Rest" wert={m => m.anteil_wolter || 0} />
                  <MonatsZeile label="Zugewiesen" wert={m => m.zugewiesen_wolter || 0} stil={zeileStufe} />
                  <MonatsZeile label="Bereits erhalten (Bezüge, Rena Wolter, Firmenwagen, weitere Zuwendungen)" wert={m => -m.gf.gesamt} />
                  <MonatsZeile label="Noch offen" wert={m => m.offen_wolter || 0} stil={zeileErgebnis} />
                  <GruppenZeile label="Eike Priel" />
                  <MonatsZeile label="Zugewiesen" wert={m => m.anteil_priel || 0} stil={zeileStufe} />
                  <MonatsZeile label="Noch offen" wert={m => m.anteil_priel || 0} stil={zeileErgebnis} />
                  <GruppenZeile label="Zusammen" />
                  <MonatsZeile label="Noch offen" wert={m => (m.offen_wolter || 0) + (m.anteil_priel || 0)} stil={zeileStufe} />
                </tbody>
              </table>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', padding: '16px 20px', borderTop: '1px solid #E5E7EB', backgroundColor: '#FAFBFC' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Tim Wolter noch offen</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: farbeBetrag(offenWolter) }}>{euro(offenWolter)}</div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                  Zugewiesen {euro(zugewiesenWolter)}, bereits erhalten {euro(erhaltenWolter)}
                  {' '}(Bezüge {euro(summe(m => m.gf.personal))}, Firmenwagen{' '}
                  <span style={{ color: '#2563EB', cursor: 'pointer' }} onClick={() => oeffneDetail('gf_firmenwagen')}>{euro(summe(m => m.gf.firmenwagen))}</span>, weitere Zuwendungen{' '}
                  <span style={{ color: '#2563EB', cursor: 'pointer' }} onClick={() => oeffneDetail('gf_sonstiges')}>{euro(summe(m => m.gf.sonstiges))}</span>)
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Eike Priel noch offen</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: farbeBetrag(anteilPriel) }}>{euro(anteilPriel)}</div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>Zugewiesen {euro(anteilPriel)}, bisher nichts erhalten</div>
              </div>
            </div>
            <div style={{ padding: '10px 16px', fontSize: '12px', color: '#9CA3AF', borderTop: '1px solid #E5E7EB' }}>
              Die Gewerbesteuer ist eine Rückstellung je Monat mit anteiligem Freibetrag ({euro(daten.einstellungen.gewst_freibetrag)} im Jahr) und
              Messzahl {zahl(daten.einstellungen.gewst_messzahl)} %. Sie rechnet mit den tatsächlichen Kosten; die Vergütung von Tim Wolter
              mindert den Gewerbeertrag nicht. Die endgültige Steuer ergibt sich aus dem Jahreswert. Die Zuweisungen ergeben zusammen den
              Betrag zur Verteilung; die offenen Beträge zusammen das, was nach Steuer, Tilgung und den Zahlungen an Tim Wolter noch in der Firma ist.
            </div>
          </div>

          {/* Was am Ende übrig bleibt */}
          <div style={karte}>
            <div style={kartenKopf}>Was am Ende übrig bleibt (Prognose)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px', padding: '16px 20px' }}>
              {[
                {
                  name: 'Tim Wolter',
                  anteil: summe(m => m.offen_wolter || 0),
                  est: summe(m => (m.einkommensteuer?.wolter.tariflich || 0) + (m.einkommensteuer?.wolter.ermaessigung || 0)),
                  soli: summe(m => m.einkommensteuer?.wolter.soli || 0),
                  bleibt: summe(m => m.einkommensteuer?.wolter.offen_nach_steuern || 0)
                },
                {
                  name: 'Eike Priel',
                  anteil: summe(m => m.anteil_priel || 0),
                  est: summe(m => (m.einkommensteuer?.priel.tariflich || 0) + (m.einkommensteuer?.priel.ermaessigung || 0)),
                  soli: summe(m => m.einkommensteuer?.priel.soli || 0),
                  bleibt: summe(m => m.einkommensteuer?.priel.offen_nach_steuern || 0)
                }
              ].map(g => (
                <div key={g.name} style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', fontWeight: 700, color: '#111827', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>{g.name}</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <tbody>
                      <tr>
                        <td style={tdLinks}>Anteil, der zugewiesen wird</td>
                        <td style={{ ...td, color: farbeBetrag(g.anteil) }}>{euro(g.anteil)}</td>
                      </tr>
                      <tr>
                        <td style={tdLinks}>Einkommensteuer darauf</td>
                        <td style={{ ...td, color: farbeBetrag(-g.est) }}>{euro(-g.est)}</td>
                      </tr>
                      <tr>
                        <td style={tdLinks}>Solidaritätszuschlag darauf</td>
                        <td style={{ ...td, color: farbeBetrag(-g.soli) }}>{euro(-g.soli)}</td>
                      </tr>
                      <tr style={zeileErgebnis}>
                        <td style={tdLinks}>Bleibt nach Steuern</td>
                        <td style={{ ...td, color: farbeBetrag(g.bleibt), fontWeight: 700 }}>{euro(g.bleibt)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            <div style={{ margin: '0 20px 16px', padding: '10px 14px', backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', color: '#92400E', fontSize: '13px' }}>
              Nicht in dieser Rechnung: Auf die Bezüge von Tim Wolter fällt voraussichtlich zusätzlich{' '}
              <strong>{euro(summe(m => m.einkommensteuer?.wolter.hinweis_nachzahlung_bezuege || 0))}</strong>{' '}
              Einkommensteuer mit Solidaritätszuschlag an, weil die Lohnsteuer nach Steuerklasse unter dem Grenzsteuersatz liegt
              und der Kindergartenzuschuss im Lohn steuerfrei ist.
            </div>

            {/* Je Monat */}
            <div style={{ overflowX: 'auto', borderTop: '1px solid #E5E7EB' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <MonatsKopf />
                <tbody>
                  <GruppenZeile label="Tim Wolter" />
                  <MonatsZeile label="Anteil, der zugewiesen wird" wert={m => m.offen_wolter || 0} />
                  <MonatsZeile label="Einkommensteuer darauf" wert={m => -((m.einkommensteuer?.wolter.tariflich || 0) + (m.einkommensteuer?.wolter.ermaessigung || 0))} />
                  <MonatsZeile label="Solidaritätszuschlag darauf" wert={m => -(m.einkommensteuer?.wolter.soli || 0)} />
                  <MonatsZeile label="Bleibt nach Steuern" wert={m => m.einkommensteuer?.wolter.offen_nach_steuern || 0} stil={zeileErgebnis} />
                  <GruppenZeile label="Eike Priel" />
                  <MonatsZeile label="Anteil, der zugewiesen wird" wert={m => m.anteil_priel || 0} />
                  <MonatsZeile label="Einkommensteuer darauf" wert={m => -((m.einkommensteuer?.priel.tariflich || 0) + (m.einkommensteuer?.priel.ermaessigung || 0))} />
                  <MonatsZeile label="Solidaritätszuschlag darauf" wert={m => -(m.einkommensteuer?.priel.soli || 0)} />
                  <MonatsZeile label="Bleibt nach Steuern" wert={m => m.einkommensteuer?.priel.offen_nach_steuern || 0} stil={zeileErgebnis} />
                </tbody>
              </table>
            </div>

            {/* Herleitung der Einkommensteuer */}
            <div
              onClick={() => setSteuerHerleitungOffen(o => !o)}
              style={{ padding: '10px 16px', borderTop: '1px solid #E5E7EB', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#2563EB' }}
            >
              {steuerHerleitungOffen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              So wird die Einkommensteuer berechnet
            </div>
            {steuerHerleitungOffen && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <MonatsKopf />
                    <tbody>
                      <GruppenZeile label="Tim Wolter" />
                      <MonatsZeile label="Anteil, der zugewiesen wird" wert={m => m.offen_wolter || 0} />
                      <MonatsZeile label="plus weitere Zuwendungen (nicht über den Lohn versteuert)" wert={m => m.gf.sonstiges} />
                      <MonatsZeile label="plus Anteil an Gewerbesteuer und Tilgung (mindern den steuerlichen Gewinn nicht)" wert={m => (m.einkommensteuer?.wolter.basis || 0) - (m.offen_wolter || 0) - m.gf.sonstiges} />
                      <MonatsZeile label="Steuerpflichtiger zusätzlicher Gewinn" wert={m => m.einkommensteuer?.wolter.basis || 0} stil={zeileStufe} />
                      <MonatsZeile label={`Einkommensteuer ${zahl(daten.einstellungen.est_satz_wolter)} %`} wert={m => m.einkommensteuer?.wolter.tariflich || 0} />
                      <MonatsZeile label="Anrechnung Gewerbesteuer (§ 35 EStG)" wert={m => m.einkommensteuer?.wolter.ermaessigung || 0} />
                      <MonatsZeile label="Einkommensteuer darauf" wert={m => (m.einkommensteuer?.wolter.tariflich || 0) + (m.einkommensteuer?.wolter.ermaessigung || 0)} stil={zeileStufe} />
                      <MonatsZeile label={`Solidaritätszuschlag ${zahl(daten.einstellungen.soli_satz)} %`} wert={m => m.einkommensteuer?.wolter.soli || 0} />
                      <GruppenZeile label="Eike Priel" />
                      <MonatsZeile label="Steuerpflichtiger zusätzlicher Gewinn" wert={m => m.einkommensteuer?.priel.basis || 0} stil={zeileStufe} />
                      <MonatsZeile label={`Einkommensteuer ${zahl(daten.einstellungen.est_satz_priel)} %`} wert={m => m.einkommensteuer?.priel.tariflich || 0} />
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: '10px 16px', fontSize: '12px', color: '#9CA3AF', borderTop: '1px solid #E5E7EB' }}>
                  Steuerpflichtig ist der Gewinnanteil, nicht die Auszahlung. Deshalb zählen auch die weiteren Zuwendungen und der Anteil an
                  Gewerbesteuer und Tilgung mit. Gerechnet wird mit dem Grenzsteuersatz. Die Anrechnung beträgt das{' '}
                  {zahl(daten.einstellungen.gewst_anrechnung_faktor)}-fache des anteiligen Gewerbesteuer-Messbetrags nach dem allgemeinen
                  Gewinnverteilungsschlüssel, höchstens die anteilige Gewerbesteuer, und mindert auch die Bemessungsgrundlage des
                  Solidaritätszuschlags; bei 0 % entfällt sie.
                </div>
              </>
            )}
          </div>

          {/* Tilgungen */}
          <div style={karte}>
            <div style={kartenKopf}>Tilgungen Darlehen Dr. Priel AG {jahr}</div>
            <div style={{ padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', borderBottom: '1px solid #E5E7EB' }}>
              <select value={tilgungMonat} onChange={e => setTilgungMonat(Number(e.target.value))} style={auswahlfeld} disabled={tilgungArbeitet}>
                {MONATE.slice(1).map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
              </select>
              <input
                type="text"
                inputMode="decimal"
                placeholder="Betrag in €"
                value={tilgungBetrag}
                onChange={e => setTilgungBetrag(e.target.value)}
                style={{ ...auswahlfeld, width: '140px', textAlign: 'right' }}
                disabled={tilgungArbeitet}
              />
              <input
                type="text"
                placeholder="Notiz (optional)"
                value={tilgungNotiz}
                onChange={e => setTilgungNotiz(e.target.value)}
                style={{ ...auswahlfeld, flex: '1', minWidth: '200px' }}
                disabled={tilgungArbeitet}
              />
              <button
                onClick={speichereTilgung}
                disabled={tilgungArbeitet}
                style={{ padding: '7px 14px', backgroundColor: tilgungArbeitet ? '#9CA3AF' : '#2563EB', color: '#FFFFFF', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 500, cursor: tilgungArbeitet ? 'wait' : 'pointer' }}
              >
                Tilgung eintragen
              </button>
            </div>
            {tilgungFehler && (
              <div style={{ padding: '10px 20px', fontSize: '13px', color: '#991B1B', backgroundColor: '#FEF2F2' }}>{tilgungFehler}</div>
            )}
            {daten.tilgungen.length === 0 ? (
              <div style={{ padding: '16px 20px', fontSize: '13px', color: '#9CA3AF' }}>Für {jahr} ist keine Tilgung eingetragen.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th style={thLinks}>Monat</th>
                    <th style={th}>Betrag</th>
                    <th style={thLinks}>Notiz</th>
                    <th style={thLinks}>Erfasst</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {daten.tilgungen.map(t => (
                    <tr key={t.id}>
                      <td style={tdLinks}>{MONATE[t.monat]}</td>
                      <td style={td}>{euro(t.betrag)}</td>
                      <td style={{ ...tdLinks, color: '#374151', whiteSpace: 'normal' }}>{t.notiz || ''}</td>
                      <td style={{ ...tdLinks, color: '#6B7280' }}>{formatDatum(t.erfasst_am)}</td>
                      <td style={td}>
                        <button
                          onClick={() => loescheTilgung(t.id)}
                          disabled={tilgungArbeitet}
                          title="Tilgung löschen"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: '2px' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Einzelbuchungen einer Kategorie */}
      {detail && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', width: '94%', maxWidth: '1400px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                {KATEGORIE_LABEL[detail]}, {MONATE[von]} bis {MONATE[bis]} {jahr}
              </span>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <X size={18} color="#6B7280" />
              </button>
            </div>
            <div style={{ overflowY: 'auto' }}>
              {detailLaedt && (
                <div style={{ padding: '30px', textAlign: 'center', color: '#6B7280' }}>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              )}
              {detailFehler && <div style={{ padding: '16px 20px', color: '#991B1B', fontSize: '13px' }}>{detailFehler}</div>}
              {!detailLaedt && !detailFehler && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th style={thLinks}>Monat</th>
                      <th style={thLinks}>Beleg</th>
                      <th style={thLinks}>Konto</th>
                      <th style={thLinks}>Gegenkonto</th>
                      <th style={thLinks}>Buchungstext</th>
                      <th style={th}>Betrag</th>
                      <th style={thLinks}>Zuordnung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailBuchungen.map(b => (
                      <tr key={`${b.id}`}>
                        <td style={tdLinks}>{MONATE_KURZ[b.leistungsmonat]} {b.leistungsjahr !== jahr ? b.leistungsjahr : ''}</td>
                        <td style={{ ...tdLinks, color: '#6B7280' }}>{formatDatum(b.belegdatum)}{b.belegfeld ? ` · ${b.belegfeld}` : ''}</td>
                        <td style={{ ...tdLinks, whiteSpace: 'normal' }}>{b.konto} {b.konto_name || ''}</td>
                        <td style={{ ...tdLinks, whiteSpace: 'normal', color: '#374151' }}>{b.gegenkonto || ''} {b.gegenkonto_name || ''}</td>
                        <td style={{ ...tdLinks, whiteSpace: 'normal', color: '#374151' }}>{b.buchungstext || ''}</td>
                        <td style={{ ...td, color: farbeBetrag(b.betrag) }}>{euro(b.betrag)}</td>
                        <td style={{ ...tdLinks, color: '#6B7280' }}>
                          {REGEL_LABEL[b.regel]}
                          {b.buchungsmonat !== b.leistungsmonat || b.buchungsjahr !== b.leistungsjahr ? `, gebucht ${MONATE_KURZ[b.buchungsmonat]} ${b.buchungsjahr}` : ''}
                        </td>
                      </tr>
                    ))}
                    <tr style={zeileStufe}>
                      <td style={tdLinks} colSpan={5}>Summe ({detailBuchungen.length} Buchungen)</td>
                      <td style={td}>{euro(r2(detailBuchungen.reduce((sum, b) => sum + b.betrag, 0)))}</td>
                      <td style={td}></td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
